import { Capacitor } from '@capacitor/core';
import { registrarRedNativa } from './conectividad';

/**
 * Preguntarle al sistema operativo si hay red, y quedarse escuchando.
 *
 * ## Por qué hace falta un plugin nativo
 *
 * `navigator.onLine` devolvió `true` con el teléfono en MODO AVIÓN. No es un
 * bug de la aplicación: el WebView de Android no siempre recibe los avisos de
 * conectividad del sistema, así que esa propiedad contesta lo último que
 * escuchó, que puede ser de hace rato.
 *
 * El plugin sí pregunta del lado nativo, donde la respuesta es la verdadera.
 *
 * ## Qué cambia
 *
 * La reacción. Sin esto, quedarse sin señal se descubre recién cuando una
 * petición vence, y hasta entonces la pantalla espera. Con esto, apenas el
 * sistema avisa que se cayó la red la aplicación ya está en el camino sin
 * conexión: sin esperar nada, sin un error que traducir.
 *
 * Al revés no manda: que el sistema diga que hay red no prueba que Supabase
 * conteste. Eso lo decide una petición de verdad, y de eso se encarga
 * `conectividad`.
 *
 * ## En el navegador no se usa
 *
 * En web el plugin no tiene nada nativo a lo que preguntar: contesta con el
 * mismo `navigator.onLine` del que se desconfía. Devolver esa respuesta
 * disfrazada de señal del sistema sería peor que no tenerla, así que en web
 * directamente no se enciende y se sigue midiendo por peticiones. El módulo se
 * carga sólo si hace falta, para no sumar el plugin al paquete web.
 */
export async function escucharRedDelSistema(): Promise<() => void> {
  if (!Capacitor.isNativePlatform()) return () => {};

  try {
    const { Network } = await import('@capacitor/network');
    const estado = await Network.getStatus();
    registrarRedNativa(estado.connected);

    const oyente = await Network.addListener('networkStatusChange', (s) => {
      registrarRedNativa(s.connected);
    });
    return () => void oyente.remove();
  } catch {
    // Sin plugin se sigue funcionando como antes: la conectividad se deduce de
    // las peticiones. Se pierde la reacción inmediata, no la aplicación.
    return () => {};
  }
}
